import { useRef, useState, useCallback } from "react";
import { Reveal, SectionHead } from "./Reveal";

const BeforeAfter = () => {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);

  const update = useCallback((clientX) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(96, Math.max(4, pct)));
  }, []);

  return (
    <div
      ref={ref}
      data-testid="before-after-slider"
      role="slider"
      aria-label="Before and after comparison — drag to compare"
      aria-valuenow={Math.round(pos)}
      aria-valuemin={0}
      aria-valuemax={100}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "ArrowLeft") setPos((p) => Math.max(4, p - 5));
        if (e.key === "ArrowRight") setPos((p) => Math.min(96, p + 5));
      }}
      onPointerDown={(e) => {
        dragging.current = true;
        e.currentTarget.setPointerCapture?.(e.pointerId);
        update(e.clientX);
      }}
      onPointerMove={(e) => dragging.current && update(e.clientX)}
      onPointerUp={() => (dragging.current = false)}
      onPointerLeave={() => (dragging.current = false)}
      className="relative w-full aspect-[7/10] overflow-hidden cursor-ew-resize select-none touch-none focus:outline-none focus:ring-1 focus:ring-volt"
    >
      <img
        src="/assets/foam-nissan.webp"
        alt="Vehicle covered in foam during the DARK process"
        className="absolute inset-0 w-full h-full object-cover"
        loading="lazy"
        draggable={false}
      />
      <div className="absolute inset-0" style={{ clipPath: `inset(0 0 0 ${pos}%)` }}>
        <img
          src="/assets/nissan-front-clean.webp"
          alt="The same vehicle after DARK service — finished result"
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
          draggable={false}
        />
      </div>
      <div className="absolute inset-y-0 w-px bg-ivory/80" style={{ left: `${pos}%` }} aria-hidden="true">
        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 rounded-full border border-ivory/70 bg-ink/70 backdrop-blur-sm flex items-center justify-center">
          <span className="font-accent text-[8px] tracking-[0.2em] text-ivory">DRAG</span>
        </div>
      </div>
      <span className="absolute top-4 left-4 font-accent text-[9px] tracking-[0.3em] uppercase bg-ink/70 backdrop-blur-sm text-silver px-3 py-1.5">Before</span>
      <span className="absolute top-4 right-4 font-accent text-[9px] tracking-[0.3em] uppercase bg-ink/70 backdrop-blur-sm text-volt px-3 py-1.5">After</span>
    </div>
  );
};

export const Results = () => (
  <section id="results" data-testid="results-section" className="py-28 sm:py-40 px-5 sm:px-8">
    <div className="max-w-7xl mx-auto">
      <SectionHead index="03" overline="Real Results" title="Real work. Real transformation." />

      <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 mt-20 items-start">
        <Reveal>
          <p className="font-accent text-[10px] tracking-[0.35em] uppercase text-silver/50 mb-5" data-testid="story-reset-label">
            01 · Complete Reset
          </p>
          <BeforeAfter />
        </Reveal>
        <Reveal delay={0.12}>
          <p className="font-accent text-[10px] tracking-[0.35em] uppercase text-silver/50 mb-5" data-testid="story-interior-label">
            02 · Interior Restoration
          </p>
          <div className="grid grid-cols-2 gap-4">
            <figure data-testid="results-frame-interior">
              <img
                src="/assets/interior-before.webp"
                alt="Interior before restoration"
                loading="lazy"
                className="w-full aspect-[4/5] object-cover"
              />
              <figcaption className="font-accent text-[9px] tracking-[0.3em] uppercase text-silver/50 mt-3">Before</figcaption>
            </figure>
            <figure data-testid="results-frame-wheel">
              <img
                src="/assets/nissan-wheel-detail.webp"
                alt="Precision touch-point cleaning in progress"
                loading="lazy"
                className="w-full aspect-[4/5] object-cover"
              />
              <figcaption className="font-accent text-[9px] tracking-[0.3em] uppercase text-silver/50 mt-3">Process</figcaption>
            </figure>
          </div>
        </Reveal>
      </div>
    </div>

    {/* Foam — full-width editorial process moment */}
    <Reveal delay={0.05} className="mt-24 sm:mt-32">
      <div className="relative w-full overflow-hidden" data-testid="foam-feature">
        <img
          src="/assets/foam-nissan.webp"
          alt="Vehicle fully covered in foam during the DARK process"
          loading="lazy"
          className="w-full h-[60vh] sm:h-[80vh] object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/50" aria-hidden="true" />
        <p className="absolute bottom-10 sm:bottom-14 left-5 sm:left-8 font-display italic text-2xl sm:text-4xl text-ivory">
          The result starts before the finish.
        </p>
      </div>
    </Reveal>
  </section>
);
